import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import FeatureGatedButton from 'components/FeatureGatedButton';
import Surface from 'components/Surface';
import Toggle from 'components/Toggle';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import type { ReactChildren } from 'types/utility';
import { ContentFrame } from '../container';
import styles from './FormSection.module.scss';

export class SectionDetails extends React.PureComponent<{
  children?: React.ReactNode;
  title?: string;
  description?: string | React.ReactNode;
}> {
  render() {
    return (
      <div className={styles.details}>
        {!!this.props.title && <h3>{this.props.title}</h3>}
        {!!this.props.description && <p>{this.props.description}</p>}
        {this.props.children}
      </div>
    );
  }
}

export class SectionGrid extends React.PureComponent<{
  children?: React.ReactNode;
}> {
  render() {
    return <div className={styles.grid}>{this.props.children}</div>;
  }
}

interface SectionColumnProps extends Omit<React.HTMLProps<HTMLDivElement>, 'size'> {
  children?: ReactChildren;
  alignContent?: 'start' | 'end';
  size: 'one-third' | 'one-quarter' | 'half' | 'two-thirds' | 'auto';
  flex: 'initial' | 'auto';
  className?: string;
}

export class SectionColumn extends React.PureComponent<SectionColumnProps> {
  static defaultProps = {
    size: 'half',
    flex: 'auto',
  };

  render() {
    const { className, size, flex, alignContent, children, ...rest } = this.props;
    const containerClass = classNames(
      styles.column,
      styles[size],
      styles[`flex-${flex}`],
      className,
      {
        [styles.alignLeft]: alignContent === 'start',
        [styles.alignRight]: alignContent === 'end',
      }
    );

    return (
      <div {...rest} className={containerClass}>
        {children}
      </div>
    );
  }
}

interface SectionRowProps {
  className?: string;
  alignItems?: 'start' | 'end' | 'center' | 'stretch' | 'baseline';
  spaceBetween: boolean;
  border?:
    | 'top'
    | null
    | undefined
    | 'right'
    | null
    | undefined
    | 'bottom'
    | null
    | undefined
    | 'left'
    | null
    | undefined
    | 'all'
    | null
    | undefined;
  direction?: 'row' | null | undefined | 'column' | null | undefined;
  children: React.ReactNode;
  clearable: boolean;
  onClear?: () => any;
  overflowScroll: boolean;
}

export class SectionRow extends React.PureComponent<SectionRowProps> {
  static defaultProps = {
    spaceBetween: false,
    clearable: false,
    overflowScroll: false,
  };

  render() {
    // This is required because the prop is optional, and you can't coerce null | undefined to string safely
    const border = this.props.border && styles[`border-${this.props.border}`];
    const direction = this.props.direction && styles[`direction-${this.props.direction}`];
    const alignItems = this.props.alignItems && styles[`items-${this.props.alignItems}`];

    const className = classNames(
      styles.row,
      border,
      direction,
      alignItems,
      {
        [styles.justifyBetween]: this.props.spaceBetween,
        [styles.overflowScroll]: this.props.overflowScroll,
      },
      this.props.className
    );
    return (
      <div className={className}>
        {this.props.children}
        {this.props.clearable && (
          <SectionColumn flex="initial">
            <br />
            <Button size="small" onClick={this.props.onClear}>
              <FontAwesome name="times" />
            </Button>
          </SectionColumn>
        )}
      </div>
    );
  }
}

interface FormSectionProps {
  className?: string;
  removable: boolean;
  onRemove?: () => void;
  children?: React.ReactNode;
  noPadding: boolean;
  disabled?: boolean;
  toggleable?: boolean;
  //
  onChange?: (payload: { enabled: boolean }) => void;
  active?: boolean;
  isLockedFeature?: boolean;
  isEnabled?: boolean;
  /** What does button */
  enableLabel?: string;
  /** id of ContentFrame container */
  id?: string;

  buttonTestId?: string;
}

export class FormSection extends React.PureComponent<FormSectionProps> {
  static defaultProps = {
    removable: false,
    noPadding: false,
    disabled: false,
    toggleable: false,
    isEnabled: true,
    enableLabel: 'Enable',
  };

  render() {
    const showToggle = this.props.onChange && this.props.toggleable;
    const showButton = !this.props.toggleable && !this.props.isEnabled;

    const className = classNames(
      styles.container,
      {
        [styles.noPadding]: this.props.noPadding,
        [styles.disabled]: !this.props.isEnabled,
      },
      this.props.className
    );

    return (
      <ContentFrame id={this.props.id} spacing padding={false} borders>
        <Surface className={className}>
          <div className={styles.actions}>
            {showToggle && (
              <Toggle
                isLockedFeature={this.props.isLockedFeature}
                checked={this.props.isEnabled}
                onClick={() => this.props.onChange!({ enabled: !this.props.isEnabled })}
              />
            )}
            {showButton && (
              <FeatureGatedButton
                priority="low"
                isFeatureLocked={this.props.isLockedFeature}
                isActive={this.props.isEnabled}
                onClick={() => this.props.onChange?.({ enabled: !this.props.isEnabled })}
                onClickLocked={() => this.props.onChange?.({ enabled: !!this.props.isEnabled })}
                data-testid={this.props.buttonTestId}
              >
                {this.props.enableLabel}
              </FeatureGatedButton>
            )}
          </div>
          {this.props.removable && (
            <div className={styles.topRightFloater}>
              <button onClick={this.props.onRemove} disabled={this.props.disabled}>
                <FontAwesome name="times-circle" />
              </button>
            </div>
          )}
          {this.props.children}
        </Surface>
      </ContentFrame>
    );
  }
}

interface CollapsedSectionProps {
  title?: string;
  description?: string;
}

export class CollapsedSection extends React.PureComponent<CollapsedSectionProps> {
  render() {
    const { title, description } = this.props;

    return (
      <React.Fragment>
        <SectionDetails title={title} description={description}></SectionDetails>
      </React.Fragment>
    );
  }
}
